import React, { useState } from "react";
import { StatBox, StrikeZoneGrid, PageFooter } from "./components";
import { formatHand, formatPlayerInfo } from "./utils";
import { HITTER_TYPE_LABELS, WIDTH, HEIGHT } from "./constants";
import { PLAYER_HEADSHOT_FALLBACK } from "../../../config/constants";
import { SprayChartMini } from "./SprayChartMini";

const isSwitch = (batsValue) => {
  if (!batsValue) return false;
  const upper = batsValue.toUpperCase();
  return ["B", "BOTH", "S", "SWITCH"].includes(upper);
};

const TendencyTag = ({ label, color = "blue" }) => {
  const colors = {
    blue: "bg-blue-100 text-blue-700 border-blue-200",
    green: "bg-green-100 text-green-700 border-green-200",
    amber: "bg-amber-100 text-amber-700 border-amber-200",
    red: "bg-red-100 text-red-700 border-red-200",
    gray: "bg-gray-100 text-gray-600 border-gray-200",
  };
  return (
    <span className={`text-[10px] font-medium px-2 py-1 rounded border ${colors[color]}`}>
      {label}
    </span>
  );
};

const TendencyTags = ({ player }) => {
  const tags = [];

  if (player.isFirstPitchAggressive) tags.push({ label: "1st Pitch Aggressive", color: "amber" });
  if (player.isGoodWithTwoStrikes) tags.push({ label: "Good w/ 2 Strikes", color: "green" });
  if (player.isPatient) tags.push({ label: "Patient", color: "blue" });
  if (player.isFreeSwinger) tags.push({ label: "Free Swinger", color: "red" });
  
  if (player.isPullHeavy) tags.push({ label: "Pull Heavy", color: "gray" });
  if (player.isOppoHitter) tags.push({ label: "Oppo Hitter", color: "gray" });
  
  if (player.likesVelocity) tags.push({ label: "Likes Velo", color: "green" });
  if (player.likesBreaking) tags.push({ label: "Likes Breaking", color: "green" });
  if (player.strugglesVelocity) tags.push({ label: "Struggles vs Velo", color: "red" });
  if (player.strugglesBreaking) tags.push({ label: "Struggles vs Breaking", color: "red" });
  
  if (player.isStolenBaseThreat) tags.push({ label: "SB Threat", color: "blue" });
  if (player.isBuntThreat) tags.push({ label: "Bunt Threat", color: "blue" });

  if (tags.length === 0) return null;
  
  return (
    <div className="flex flex-wrap gap-1.5">
      {tags.map((tag, i) => (
        <TendencyTag key={i} label={tag.label} color={tag.color} />
      ))}
    </div>
  );
};

export const BatterPage = ({ player, stats, sprayChartData, report, guts, pageNumber, totalPages }) => {
  const [imageError, setImageError] = useState(false);
  const headshotUrl = stats?.headshot_url || PLAYER_HEADSHOT_FALLBACK;
  
  const rawBats = player.bats || stats?.bats || sprayChartData?.bats;
  const isSwitchHitter = isSwitch(rawBats);
  const bats = isSwitchHitter ? "S" : formatHand(rawBats);
  const infoParts = formatPlayerInfo(stats);

  const battingStats = stats?.batting_stats?.[0] || sprayChartData?.stats || {};

  return (
    <div 
      className="bg-white relative"
      style={{ width: WIDTH, height: HEIGHT }}
      data-player-page
    >
      <div className="bg-gradient-to-r from-blue-800 to-blue-600 px-8 py-5 flex items-center justify-between">
        <div className="flex items-center gap-5">
          <div className="w-20 h-20 rounded-xl overflow-hidden bg-blue-900 ring-2 ring-white/30 flex-shrink-0">
            {!imageError ? (
              <img 
                src={headshotUrl} 
                alt={player.name} 
                className="w-full h-full object-cover object-top"
                onError={() => setImageError(true)}
              />
            ) : (
              <div className="w-full h-full flex items-center justify-center text-white/50 text-2xl font-bold">
                {player.name?.charAt(0) || "?"}
              </div>
            )}
          </div>
          
          <div>
            <h1 className="text-2xl font-bold text-white">{player.name}</h1>
            <p className="text-blue-200 text-sm">
              {player.position || "—"} • Bats: {bats}
            </p>
            {infoParts.length > 0 && (
              <p className="text-blue-300/70 text-xs mt-0.5">
                {infoParts.join(" • ")}
              </p>
            )}
          </div>
        </div>
        
        <div className="text-right">
          <div className="text-white font-semibold">{report.teamName}</div>
          <div className="text-blue-200 text-sm">{report.year} Season</div>
        </div>
      </div>

      <div className="px-8 py-6 flex gap-8">
        <div className="flex-1">
          <h2 className="text-sm font-semibold text-blue-800 mb-3 pb-1 border-b-2 border-blue-100">
            Season Statistics
          </h2>
          <div className="grid grid-cols-6 gap-2 mb-6">
            <StatBox label="AVG" value={battingStats.avg != null ? battingStats.avg.toFixed(3) : (battingStats.ba != null ? battingStats.ba.toFixed(3) : null)} />
            <StatBox label="OBP" value={battingStats.obp != null ? battingStats.obp.toFixed(3) : (battingStats.ob_pct != null ? battingStats.ob_pct.toFixed(3) : null)} />
            <StatBox label="SLG" value={battingStats.slg != null ? battingStats.slg.toFixed(3) : (battingStats.slg_pct != null ? battingStats.slg_pct.toFixed(3) : null)} />
            <StatBox label="HR" value={battingStats.hr != null ? battingStats.hr : null} />
            <StatBox label="RBI" value={battingStats.rbi != null ? battingStats.rbi : null} />
            <StatBox label="WAR" value={battingStats.war != null ? battingStats.war.toFixed(1) : null} />
          </div>

          {player.writeup && (
            <>
              <h2 className="text-sm font-semibold text-blue-800 mb-3 pb-1 border-b-2 border-blue-100">
                Scouting Notes
              </h2>
              <div className="bg-gray-50 rounded-lg p-4 border border-gray-100">
                <p className="text-sm text-gray-700 leading-relaxed whitespace-pre-wrap">
                  {player.writeup}
                </p>
              </div>
            </>
          )}
        </div>

        <div className="w-72">
          <h2 className="text-sm font-semibold text-blue-800 mb-3 pb-1 border-b-2 border-blue-100">
            Tendencies
          </h2>
          
          <div className="space-y-3">
            {player.hitterType && (
              <div className="flex items-center gap-2">
                <span className="text-xs text-gray-500 w-20">Type:</span>
                <span className="text-sm font-medium text-gray-900">
                  {HITTER_TYPE_LABELS[player.hitterType] || player.hitterType}
                </span>
              </div>
            )}

            <TendencyTags player={player} />

            {(player.hotZones?.length > 0 || player.coldZones?.length > 0) && (
              <div className="flex gap-6 mt-4 pt-4 border-t border-gray-100">
                {player.hotZones?.length > 0 && (
                  <StrikeZoneGrid 
                    selectedZones={player.hotZones} 
                    label="Hot Zones" 
                    isHot={true}
                    bats={rawBats}
                  />
                )}
                {player.coldZones?.length > 0 && (
                  <StrikeZoneGrid 
                    selectedZones={player.coldZones} 
                    label="Cold Zones" 
                    isHot={false}
                    bats={rawBats}
                  />
                )}
              </div>
            )}

            {player.id && (
              <div className="mt-4 pt-4 border-t border-gray-100">
                {isSwitchHitter ? (
                  <div className="flex gap-2">
                    <SprayChartMini 
                      playerId={player.id} 
                      year={report.year} 
                      division={report.division}
                      prefetchedData={sprayChartData}
                      guts={guts}
                      width={135}
                      height={135}
                      handFilter={{ L: false, R: true }}
                      label="vs RHP"
                    />
                    <SprayChartMini 
                      playerId={player.id} 
                      year={report.year} 
                      division={report.division}
                      prefetchedData={sprayChartData}
                      guts={guts}
                      width={135}
                      height={135}
                      handFilter={{ L: true, R: false }}
                      label="vs LHP"
                    />
                  </div>
                ) : (
                  <SprayChartMini 
                    playerId={player.id} 
                    year={report.year} 
                    division={report.division}
                    prefetchedData={sprayChartData}
                    guts={guts}
                    width={280}
                    height={280}
                  />
                )}
              </div>
            )}
          </div>
        </div>
      </div>

      <PageFooter pageNumber={pageNumber} totalPages={totalPages} />
    </div>
  );
};
